import { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { ENDPOINTS, HANWHA_API_PATH_PREFIX } from 'src/constants/endpoints'

import { http } from '.'
import { markAuthenticationExpired } from './errors'

export interface ISetupAuthInterceptorsParams {
  getToken: () => string | undefined
  onUnauthorized: () => void
  refreshToken: () => Promise<string>
  onTokenRefreshed: (token: string) => void
}

export interface ISetupAuthInterceptorsReturn {
  eject: () => void
}

interface IAxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retried?: boolean
}

const isDefinitiveRefreshRejection = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) return false
  return error.response?.status === 401
}

const isRefreshRequest = (config?: InternalAxiosRequestConfig): boolean => {
  return config?.url === ENDPOINTS.auth.refresh
}

export const setupAuthInterceptors = (
  params: ISetupAuthInterceptorsParams,
): ISetupAuthInterceptorsReturn => {
  let refreshPromise: Promise<string> | null = null
  let unauthorizedNotified = false
  let lastToken: string | undefined

  const notifyUnauthorizedOnce = (): void => {
    if (unauthorizedNotified) return
    unauthorizedNotified = true
    params.onUnauthorized()
  }

  const requestId = http.interceptors.request.use(config => {
    if (!isRefreshRequest(config)) {
      const token = params.getToken()
      if (token !== lastToken) {
        unauthorizedNotified = false
        lastToken = token
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    config.withCredentials = true
    return config
  })

  const responseId = http.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as IAxiosRequestConfigWithRetry | undefined

      if (error.response?.status !== 401 || !config) {
        return Promise.reject(error)
      }

      if (isRefreshRequest(config)) {
        notifyUnauthorizedOnce()
        return Promise.reject(markAuthenticationExpired(error))
      }

      if (config._retried) {
        notifyUnauthorizedOnce()
        return Promise.reject(markAuthenticationExpired(error))
      }

      try {
        let isOwner = false
        if (!refreshPromise) {
          refreshPromise = params.refreshToken()
          isOwner = true
        }
        const newToken = await refreshPromise
        if (isOwner) {
          params.onTokenRefreshed(newToken)
        }
        unauthorizedNotified = false

        config._retried = true
        config.headers.Authorization = `Bearer ${newToken}`
        return http.request(config)
      } catch (refreshError) {
        // The backend refresh contract uses 401 for rejected credentials.
        // Other status codes, transport failures, and timeouts do not prove
        // the refresh credential is invalid, so retain the session for retry.
        if (isDefinitiveRefreshRejection(refreshError)) {
          notifyUnauthorizedOnce()
          return Promise.reject(markAuthenticationExpired(error))
        }
        return Promise.reject(error)
      } finally {
        refreshPromise = null
      }
    },
  )

  return {
    eject: () => {
      http.interceptors.request.eject(requestId)
      http.interceptors.response.eject(responseId)
    },
  }
}

export interface ISetupHanwhaOrgQueryInterceptorParams {
  getOrgId: () => number
}

export interface ISetupHanwhaOrgQueryInterceptorReturn {
  eject: () => void
}

const isHanwhaApiUrl = (config: InternalAxiosRequestConfig): boolean => {
  const url = config.url ?? ''
  const baseURL = config.baseURL ?? ''
  return (
    url.includes(HANWHA_API_PATH_PREFIX) ||
    `${baseURL}${url}`.includes(HANWHA_API_PATH_PREFIX)
  )
}

const mergeHanwhaOrgIdParam = (
  config: InternalAxiosRequestConfig,
  orgId: number,
): void => {
  const existing = config.params
  if (
    existing !== undefined &&
    existing !== null &&
    typeof existing === 'object' &&
    !(existing instanceof URLSearchParams)
  ) {
    config.params = { ...existing, org_id: orgId }
    return
  }
  if (existing instanceof URLSearchParams) {
    existing.set('org_id', String(orgId))
    return
  }
  config.params = { org_id: orgId }
}

export const setupHanwhaOrgQueryInterceptor = (
  params: ISetupHanwhaOrgQueryInterceptorParams,
): ISetupHanwhaOrgQueryInterceptorReturn => {
  const requestId = http.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!isHanwhaApiUrl(config)) {
        return config
      }
      mergeHanwhaOrgIdParam(config, params.getOrgId())
      return config
    },
  )

  return {
    eject: () => {
      http.interceptors.request.eject(requestId)
    },
  }
}

export interface ISetupOfflineInterceptorReturn {
  eject: () => void
}

export const setupOfflineInterceptor = (): ISetupOfflineInterceptorReturn => {
  const requestId = http.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!navigator.onLine) {
        const error = new AxiosError(
          'You appear to be offline. Please check your connection.',
          'ERR_NETWORK_OFFLINE',
          config,
        )
        return Promise.reject(error)
      }
      return config
    },
  )

  return {
    eject: () => {
      http.interceptors.request.eject(requestId)
    },
  }
}
